/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useEffect, useState, useRef } from "react";
import { HashLink } from "react-router-hash-link";
import { Button, Close } from "@cmsgov/ds-medicare-gov";
import stickybits from "stickybits";
import { cssTransition, toast, ToastContainer } from "react-toastify";
import uuid from "uuid";
import { useTranslate } from "../helpers";
import { isWindowMobileOuterWidth } from "../helpers/objectUtilities";

interface StickyTrayProps {
  className?: string;
  errorMessage?: string;
  heading: string;
  id?: string;
  items: { name: string; id: number | string }[];
  linkForSubmit?: string;
  maxItems?: number;
  mobileOpenButtonText?: string;
  onMobileToggleVisibility?: (visibility: boolean) => void;
  noItemsText?: string;
  onRemoveItem: (id: number | string) => void;
  onSubmit: () => void;
  pageLoading?: boolean;
  screenReaderText?: string;
  submitDisabled?: boolean;
  submitText: string;
  warningMessage?: string;
  notStickyOnMobile?: boolean;
}

const StickyTray: React.FunctionComponent<StickyTrayProps> = ({
  id,
  heading,
  items,
  maxItems = 3,
  pageLoading,
  errorMessage,
  warningMessage,
  onRemoveItem,
  submitDisabled,
  onSubmit,
  submitText,
  screenReaderText,
  noItemsText,
  linkForSubmit,
  mobileOpenButtonText,
  onMobileToggleVisibility = (): void => {},
  notStickyOnMobile,
  ...props
}) => {
  const t = useTranslate();
  const TOAST_DURATION = 5000;
  const [toastId, setToastId] = useState("");
  const [isOpen, _setIsOpen] = useState(false);
  const isOpenRef = useRef(isOpen);
  const setIsOpen = (value: boolean): void => {
    isOpenRef.current = value;
    _setIsOpen(value);
  };
  const [tabIndex, setTabIndex] = useState(0);
  const [stickyBit, setStickyBit] = useState<
    { update: () => void } | undefined
  >(undefined);

  const toggleVisibility = (): void => {
    const visibility = !isOpen;
    setIsOpen(visibility);
    onMobileToggleVisibility(visibility);
  };

  let lastWindowArea = window.outerHeight * window.outerWidth;

  const onResize = (): void => {
    const area = window.outerHeight * window.outerWidth;
    if (lastWindowArea !== area) {
      lastWindowArea = area;

      setTabIndex(
        !isWindowMobileOuterWidth(window) || isOpenRef.current ? 0 : -1
      );

      if (stickyBit) {
        stickyBit.update();
        window.scrollBy(0, 1);
      }
    }
  };

  useEffect(() => {
    setToastId(uuid());
    const identifier = id ? `#${id}` : ".js-stickybits";
    setStickyBit(stickybits(identifier, { verticalPosition: "bottom" }));
    window.scrollBy(0, 1);
    window.addEventListener("resize", onResize);
    return (): void => {
      window.removeEventListener("resize", onResize);
      document.body.classList.remove("no-scroll-on-mobile");
    };
  }, []);

  useEffect(() => {
    if (stickyBit) {
      stickyBit.update();
      window.scrollBy(0, 1);
    }
  }, [pageLoading]);

  useEffect(() => {
    toast.dismiss();
  }, [items.length]);

  useEffect(() => {
    if (isOpen) {
      if (!notStickyOnMobile) {
        document.body.classList.add("no-scroll-on-mobile");
      }
      setTabIndex(0);
    } else {
      document.body.classList.remove("no-scroll-on-mobile");
      setTabIndex(isWindowMobileOuterWidth(window) ? -1 : 0);
    }
  }, [isOpen]);

  errorMessage &&
    toast(
      <div className="ds-u-fill--error-lightest ds-u-padding--2">
        {errorMessage}
      </div>,
      {
        toastId,
        containerId: "stickyTrayToastContainer",
        autoClose: TOAST_DURATION,
        className: "ds-u-padding--0",
        type: toast.TYPE.ERROR,
      }
    );

  warningMessage &&
    toast(
      <div className="ds-u-fill--primary-alt-lightest ds-u-padding--2">
        {warningMessage}
      </div>,
      {
        toastId,
        containerId: "stickyTrayToastContainer",
        autoClose: TOAST_DURATION,
        className: "ds-u-padding--0",
      }
    );

  const emptyItems: string[] = [];
  emptyItems.length = maxItems - items.length;
  emptyItems.fill("");

  return (
    <>
      {!notStickyOnMobile && (
        <div
          className={`StickyTray__background${isOpen ? "" : " closed"}`}
          onClick={(): void => setIsOpen(false)}
        />
      )}
      <div
        {...props}
        id={id}
        className={`StickyTray js-stickybits ${
          props.className ? props.className : ""
        }${isOpen ? "" : " closed"}${
          notStickyOnMobile ? " not-sticky-on-mobile" : ""
        }`}
        role="region"
        aria-live="polite"
        data-testid="StickyTray"
        style={{ border: "none" }}
      >
        <ToastContainer
          enableMultiContainer
          containerId="stickyTrayToastContainer"
          autoClose={TOAST_DURATION}
          closeOnClick={false}
          draggable={false}
          hideProgressBar
          newestOnTop={false}
          position={toast.POSITION.BOTTOM_CENTER}
          rtl={false}
          className="StickyTray__toast"
          transition={cssTransition({
            enter: "zoomIn",
            exit: "zoomOut",
            duration: 400,
          })}
        />
        <div className="StickyTray__body">
          <div className="StickyTray__content">
            <div className="StickyTray__flex">
              <div
                className="StickyTray__header"
                id={id ? `${id}-sticky-tray-heading` : undefined}
                tabIndex={-1}
                onClick={(): void => {
                  if (isOpen) {
                    setIsOpen(false);
                  }
                }}
              >
                <div className="StickyTray__heading">
                  <span>{heading}</span>
                  <span className="StickyTray__mobile_close">
                    <Button
                      className="ds-c-button--inverse ds-u-padding--2"
                      variation="transparent"
                      onClick={toggleVisibility}
                      tabIndex={tabIndex}
                      aria-label={t("close")}
                    >
                      <Close />
                    </Button>
                  </span>
                </div>
                {!!items.length && (
                  <Button
                    className="StickyTray__mobile_open ds-c-button--primary-alt ds-c-button--inverse"
                    onClick={toggleVisibility}
                  >
                    {mobileOpenButtonText}
                  </Button>
                )}
                {screenReaderText && (
                  <span className="ds-u-visibility--screen-reader">
                    {screenReaderText}
                  </span>
                )}
              </div>
              {items.length ? (
                <ul
                  className={`StickyTray__list${isOpen ? "" : " closed"}`}
                  role="list"
                >
                  {items.map(item => (
                    <li
                      className="StickyTray__list_item"
                      key={item.id}
                      role="listitem"
                    >
                      <span>{item.name}</span>
                      <Button
                        variation="transparent"
                        className="StickyTray__remove"
                        onClick={(): void => {
                          if (items.length === 1) {
                            setIsOpen(false);
                          }
                          onRemoveItem(item.id);
                        }}
                        aria-label={`${t("remove")} ${item.name}`}
                        tabIndex={tabIndex}
                      >
                        <Close />
                      </Button>
                    </li>
                  ))}
                  {emptyItems.map((_item, i) => (
                    <li
                      className="StickyTray__list_item StickyTray__list_item_empty"
                      key={`open-spot-${i}`}
                      aria-hidden
                    >
                      &nbsp;
                    </li>
                  ))}
                </ul>
              ) : null}
              {!items.length ? (
                <span className="ds-u-margin-right--2">{noItemsText}</span>
              ) : null}
              {linkForSubmit ? (
                <div>
                  <HashLink
                    to={linkForSubmit}
                    className="StickyTray__action ds-c-button ds-c-button--big"
                    onClick={onSubmit}
                  >
                    {submitText}
                  </HashLink>
                </div>
              ) : (
                <Button
                  className="StickyTray__action"
                  size="big"
                  disabled={submitDisabled}
                  onClick={onSubmit}
                >
                  {submitText}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default StickyTray;
